<template>
    <section class="deals-section-wrapper">
        <div class="section-header">
            <h4>{{ $t("message.deals") }}</h4>
        </div>

        <div v-if="deals.length" class="deals-section">
            <div
                v-for="deal in deals"
                :key="deal.id"
                class="deal-row"
                :class="{ 'selected-row': isSelected(deal.id) }"
                @click="() => handleDealClick(deal)"
            >
                <div class="deal-icon">
                    <i class="fas fa-handshake"></i>
                </div>
                <div class="deal-info">
                    <div class="deal-name">
                        <i class="material-icons">sell</i>
                        <span>{{ deal.Deal_Name }}</span>
                    </div>
                    <div class="deal-stage">
                        <i class="material-icons">linear_scale</i>
                        <span>{{ deal.Stage }}</span>
                    </div>
                    <div class="deal-footer">
                        <div class="deal-owner">
                            <i class="material-icons">person</i>
                            <span>{{ $_.get(deal, "Owner.name", "") }}</span>
                        </div>
                        <!--                        <div class="deal-amount">-->
                        <!--                            <i class="material-icons">payments</i>-->
                        <!--                            <span>{{ formatAmount(deal.Amount) }}</span>-->
                        <!--                        </div>-->
                    </div>
                    <div class="deal-date">
                        <i class="material-icons">event</i>
                        <span>{{ formatDate(deal.Created_Time) }}</span>
                    </div>
                </div>
                <div class="deal-selection" @click.stop="toggleDealSelection(deal)">
                    <span class="selection-circle" :class="{ selected: isSelected(deal.id) }"></span>
                </div>
                <SupsisButton class="view-deal-btn" icon="open_in_new" @click.stop="gotoDealOnZoho(deal.id)" />
            </div>
        </div>

        <div v-else-if="loading" class="loading-container">
            <span>{{$t("message.loadingDeals")}}</span>
            <BucketSpinner class="large-spinner" />
        </div>

        <div v-else class="empty-deals-container">
            <span class="material-icons empty-icon"> assignment </span>
            <p class="empty-text">{{ $t("message.noDealsFound") }}</p>
        </div>

        <Modal2
            v-if="selectedDeal"
            title="Deal Details"
            :showFooter="false"
            @cancel="handleModalClose"
            className="deal-viewer-modal"
        >
            <DealViewer :deal="selectedDeal" @connect-zoho="$emit('connect-zoho')" />
        </Modal2>
    </section>
</template>
<script>
import Vue from "vue";
import DealViewer from "./DealViewer.vue";
import { mapState } from "vuex";
import BucketSpinner from "@/views/Reports/common/spinners/BucketSpinner.vue";
import Modal2 from "@/components/common/Modal2.vue";
import { ZOHO_API } from "../../../../api/zoho";
import SupsisButton from "@/views/Pages/Settings/components/SupsisButton.vue";

export default Vue.extend({
    name: "ZohoDealsSection",
    components: {
        BucketSpinner,
        Modal2,
        DealViewer,
        SupsisButton,
    },
    props: {
        contact: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            deals: [],
            loading: false,
            selectedDeal: null,
        };
    },
    async created() {
        await this.fetchDeals();
    },
    computed: {
        ...mapState({
            zohoChannelExists: (state) => !!state.channel.channels.find((x) => x.type === "zoho"),
            zohoChannel: (state) => state.channel.channels.find((x) => x.type === "zoho"),
            selectedDeals: (state) => state.zoho.selectedDeals,
        }),
    },
    methods: {
        async fetchDeals() {
            this.loading = true;
            try {
                const contactId = this.contact._id;
                const zohoContactId = this.contact.zohoInfo?.contactId;
                const phone = this.contact.whatsappNumber || this.contact.phone;
                const deals = zohoContactId
                    ? await ZOHO_API.searchDealsByContactId(zohoContactId)
                    : await ZOHO_API.searchDealsByPhone(phone);
                if (this.contact?._id === contactId) {
                    this.deals = Array.isArray(deals) ? deals : [];
                }
            } catch (error) {
                console.error("Deals yüklenirken hata:", error);
            } finally {
                this.loading = false;
            }
        },
        toggleDealSelection(deal) {
            if (this.isSelected(deal.id)) {
                this.deselectDeal(deal.id);
            } else {
                this.selectDeal(deal.id);
            }
        },
        isSelected(dealId) {
            return this.selectedDeals.includes(dealId);
        },
        selectDeal(dealId) {
            this.$store.dispatch("zoho/setSelectedDeals", [...this.selectedDeals, dealId]);
        },
        deselectDeal(dealId) {
            this.$store.dispatch(
                "zoho/setSelectedDeals",
                this.selectedDeals.filter((id) => id !== dealId)
            );
        },
        handleDealClick(deal) {
            this.selectedDeal = deal;
        },
        handleModalClose() {
            this.selectedDeal = null;
        },
        formatAmount(amount) {
            return new Intl.NumberFormat("tr-TR", {
                style: "currency",
                currency: "TRY",
            }).format(amount);
        },
        formatDate(dateString) {
            if (!dateString) return "";
            return new Date(dateString).toLocaleDateString("tr-TR", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        },
        gotoDealOnZoho(dealId) {
            const orgId = _.get(this.zohoChannel, "payload.organization.domain_name");
            if (orgId && dealId) {
                window.open(`https://crm.zoho.com/crm/${orgId}/tab/Potentials/${dealId}`, "_blank");
            }
        },
    },
    watch: {
        contact: {
            handler() {
                this.deals = [];
                this.fetchDeals();
                this.$store.dispatch("zoho/setSelectedDeals", []);
            },
            deep: true,
        },
    },
    beforeDestroy() {
        this.$store.dispatch("zoho/setSelectedDeals", []);
    },
});
</script>

<style scoped lang="scss">
.deals-section-wrapper {
    .section-header {
        margin-bottom: 12px;

        h4 {
            font-size: 16px;
            font-weight: 500;
            color: #4a5568;
            margin: 0;
        }
    }

    .loading-container {
        min-height: 100px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        svg {
            width: 60px !important;
            height: 60px !important;
        }
    }

    .deals-section {
        .deal-row {
            display: flex;
            padding: 12px;
            border-bottom: 1px solid #eee;
            cursor: pointer;
            transition: background-color 0.2s;
            position: relative;

            &:hover {
                background-color: #f5f5f5;
            }

            &.selected-row {
                background-color: #e6f0ff;
            }

            .deal-icon {
                margin-right: 12px;
                color: #2c5282;
                padding-top: 4px;
            }

            .deal-info {
                flex: 1;

                .deal-name,
                .deal-stage,
                .deal-footer > div,
                .deal-date {
                    display: flex;
                    align-items: center;
                    gap: 6px;

                    i.material-icons {
                        font-size: 18px;
                        color: #64748b;
                    }
                }

                .deal-name {
                    font-weight: 500;
                    font-size: 15px;
                    color: #2d3748;
                    margin-bottom: 4px;
                }

                .deal-stage {
                    display: inline-flex;
                    background: #e2e8f0;
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-size: 0.85em;
                    color: #4a5568;
                    margin-bottom: 4px;
                }

                .deal-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 0.9em;
                    margin-bottom: 4px;

                    .deal-owner {
                        color: #718096;
                    }

                    .deal-amount {
                        font-weight: 500;
                        color: #2c5282;
                    }
                }

                .deal-date {
                    font-size: 0.85em;
                    color: #94a3b8;
                }
            }

            .deal-selection {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 24px;
                height: 24px;

                .selection-circle {
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    border: 2px solid #ccc;
                    transition: background-color 0.2s, border-color 0.2s;

                    &.selected {
                        background-color: #2c5282;
                        border-color: #2c5282;
                    }
                }
            }

            .view-deal-btn {
                position: absolute;
                bottom: 3px;
                padding: 0;
                right: -20px;
            }
        }
    }

    .loading {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 20px;
    }

    .no-deals {
        text-align: center;
        padding: 20px;
        color: #666;
    }

    .empty-deals-container {
        min-height: 100px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 20px;
        color: #94a3b8;

        .empty-icon {
            font-size: 36px;
            margin-bottom: 8px;
        }

        .empty-text {
            font-size: 14px;
            text-align: center;
            margin: 0;
        }
    }
}

.deal-viewer-modal {
    .ant-modal {
        width: 700px !important;
    }
}
</style>
